/**
 * Display formatting for the decimal STRINGS the API speaks (DECISION.md
 * §2.1: money never exists as a JS number, so nothing here parses — grouping
 * is done on the string).
 *
 * THIS FILE IS THE ONE COPY. `apps/web/src/lib/format.ts` re-exports it, so
 * the figure printed on a PDF and the figure on the screen behind it are
 * produced by the same lines. A second implementation that agreed today would
 * disagree the first time either was edited, and "the PDF says 2,934.00 and
 * the screen says 2934" is exactly the class of bug this package exists to
 * make impossible.
 *
 * ── NUMERALS, and why they are Latin in Arabic too ──────────────────────────
 * PART 4 §4.10 / FIN-03 §15: "figures, codes and dates stay left-to-right".
 * The product renders every amount as an LTR island (`<bdi dir="ltr">` on
 * screen, `.ltr` here) with WESTERN digits in both languages. That is not an
 * oversight about Arabic — Eastern Arabic-Indic digits (٠١٢٣) are correct
 * Arabic typography for prose, but an invoice total is a figure a bank clerk,
 * an auditor and a tax authority all have to read, and the Levant and Gulf
 * markets this product serves print financial figures in Latin digits.
 * Choosing them is therefore a decision, recorded here, and NOT something to
 * "fix" by handing the number to Intl with `numberingSystem: 'arab'`.
 *
 * What the Arabic document DOES get from the browser is bidi: the figure sits
 * in an isolated LTR run inside right-to-left text, so "$2,934.00" never comes
 * out as "00.439,2$" and a minus sign never migrates to the wrong end.
 */
/** BR-SAL-00-13's lesson: the symbol comes from the document's currency. */
const CURRENCY_SYMBOLS = {
    USD: '$',
    EUR: '€',
    GBP: '£',
};
/**
 * `U+002D HYPHEN-MINUS` — the spec's own character, and the one a spreadsheet
 * understands.
 *
 * IT WAS `U+2212 MINUS SIGN` FOR THREE DAYS, and the comment that defended it
 * was wrong on the product's own rules. It read: *"the glyph the digits are
 * designed around in a tabular font, so a column of negatives lines up."*
 * `GEN-30` says money columns are "aligned to the TRAILING edge" and set in
 * "the monospace figure font". Both halves kill the argument — in a monospace
 * face every glyph including the hyphen is one advance wide, and in a
 * trailing-aligned column the sign sits at the RAGGED edge, where its width
 * cannot affect what lines up. The alignment the comment was protecting is
 * produced by `tabular-nums` on the digits, and the sign never enters into it.
 *
 * `N-3` and `EC-19-03` both write the example as `-$46,866.91`, and
 * EC-19-03's is inside a code span: in a FORMAT specification, a backticked
 * example is the format. The document's only two `U+2212`s are arithmetic
 * prose (`44,131.09 − 90,998.00 = −46,866.91`), which is typesetting.
 *
 * The character was never chosen against the spec — it was typed into the
 * first money formatter on 2026-08-06 (`3cf3cb5`, MD-01 partners) and carried
 * forward verbatim ever since. There is no `Intl.NumberFormat` anywhere in this
 * product's money path, so there was no locale default to inherit and nothing
 * to argue with: it is one character, and now it is the right one.
 *
 * WHAT IT COST WHILE IT WAS WRONG is smaller than it looks, and the reason is
 * worth keeping: every MACHINE-READABLE path already converted it. `bareNumber`
 * turns it back into ASCII for the .xlsx export and always did; CSV and the
 * data-transfer grid carry raw decimal strings and never see a display string
 * at all. The exposure was the one place code cannot reach — a figure a person
 * SELECTS ON SCREEN and pastes into a spreadsheet, which arrives as text.
 *
 * NOT to be confused with `ledgerQty` in `features/manufacturing/mfg-format.ts`,
 * which keeps `U+2212` deliberately and correctly: that column prints a leading
 * `+` on every positive row, so the signs are read against each other in a
 * FIXED position rather than at a ragged edge, and it is a quantity, which N-3
 * does not govern.
 */
const MINUS = '-';
/** "2934" | "2934.5" | "-12.3" → "2,934.00" / "2,934.50" / "-12.30". */
export function groupAmount(amount, decimals = 2) {
    const negative = amount.startsWith('-');
    const bare = negative ? amount.slice(1) : amount;
    const [integerRaw = '0', fractionRaw = ''] = bare.split('.');
    const integer = integerRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    /**
     * `decimals: 0` is BR-DSH-10-15's summary card — "summary-card values must
     * be rounded to WHOLE CURRENCY UNITS. Values inside detail tables must show
     * 2 decimals." It DROPS the fraction and never rounds with it: the caller
     * has already rounded through `roundToWholeUnits` (N-6, half-up, decimal
     * throughout), and rounding twice in two places is how two screens come to
     * disagree about the same figure by one unit.
     *
     * The default is 2 and nothing that does not ask for 0 can behave
     * differently, which is the point of adding a parameter rather than a
     * second formatter: there is still exactly one place the product turns an
     * amount into digits.
     */
    if (decimals === 0)
        return `${negative ? MINUS : ''}${integer}`;
    const fraction = `${fractionRaw}00`.slice(0, 2);
    return `${negative ? MINUS : ''}${integer}.${fraction}`;
}
/**
 * "2934.00", "USD" → "$2,934.00"; unknown currencies keep their code.
 *
 * N-3, and EC-19-03 quoting the same figure: "the currency sign goes in front
 * of the number, and A MINUS SIGN IN FRONT OF THE CURRENCY SIGN: -$46,866.91.
 * Brackets must not be used for negatives."
 *
 * This used to prefix the symbol to whatever `groupAmount` returned, which put
 * the sign between the symbol and the digits — `$-46,866.91`. Both halves of
 * N-3 were individually satisfied and the pair was wrong, which is why it
 * survived: nothing about `$-46,866.91` looks like a bug until you put it
 * beside the sentence. So the sign is lifted off the grouped magnitude and put
 * back on the OUTSIDE, ahead of symbol or code alike.
 */
export function formatMoney(amount, currency, decimals = 2) {
    const grouped = groupAmount(amount, decimals);
    if (!currency)
        return grouped;
    const negative = grouped.startsWith(MINUS);
    const magnitude = negative ? grouped.slice(MINUS.length) : grouped;
    const symbol = CURRENCY_SYMBOLS[currency];
    const withCurrency = symbol ? `${symbol}${magnitude}` : `${currency} ${magnitude}`;
    return negative ? `${MINUS}${withCurrency}` : withCurrency;
}
/**
 * Trailing zeros off a decimal string: "12.000" → "12", "0.500" → "0.5".
 *
 * THIS IS NOT THE QUANTITY FORMATTER, and it used to be called `formatQty`,
 * which is how `N-4` came to be cited three times and enforced nowhere. The
 * rule says a quantity carries "the number of decimals the item's unit of
 * measure allows" — trimming is the OPPOSITE instruction, and a unit that
 * allows 3 decimals must print `200.000`, not `200`, because "200" in a
 * 3-decimal unit is a claim about precision the data does not support.
 *
 * The function itself is still wanted, for the decimals that are NOT
 * quantities: a percentage typed as `15.00`, a conversion factor, a rate. It
 * just has to be named for what it does, so that reaching for it on a quantity
 * looks like the mistake it is.
 */
export function trimTrailingZeros(value) {
    if (!value.includes('.'))
        return value;
    return value.replace(/\.?0+$/, '') || '0';
}
/**
 * `N-4` — "Quantities must be shown with the number of decimals the item's
 * UNIT OF MEASURE allows, and ALWAYS with the unit written next to them."
 *
 * ── THE DECIMALS ───────────────────────────────────────────────────────────
 * `precision` is `units.precision` (MD-12 field 9, `ERR-MD-12-07`: 0..3), and
 * it belongs to the item's STOCK unit — the one the quantity is counted, held
 * and valued in (`items.stock_unit_id`, MD-03 field 17). It is not the column's
 * scale: stock columns are `numeric(18,3)` or six decimals wide, which is a
 * storage decision, while `precision` is the business one. Pieces are 0 and
 * print `4`; kilograms are 3 and print `4.000`; and `BR-MD-12-11` already
 * refuses to STORE more decimals than the unit allows, so rounding here only
 * ever discards the padding a numeric column added.
 *
 * The parameter is REQUIRED on purpose. An optional one would let a caller
 * keep the old trimming behaviour by saying nothing, which is precisely the
 * failure this rule already had: three honest citations, no enforcement.
 *
 * ── THE ROUNDING ───────────────────────────────────────────────────────────
 * Half-up (`N-6`), done on the STRING. Nothing here parses to a JS number —
 * the same discipline `groupAmount` follows and the reason `@palaccount/money`
 * exists. This is display only: no arithmetic, no `Money`, nothing that a
 * ledger figure passes through.
 */
export function formatQty(value, precision) {
    if (!Number.isInteger(precision) || precision < 0 || precision > 3) {
        // ERR-MD-12-07's range. A precision outside it is a bad row or a bad
        // caller, and quietly printing something is how a wrong quantity ships.
        throw new RangeError(`A unit allows between 0 and 3 decimals, not ${String(precision)}.`);
    }
    const negative = value.startsWith('-');
    const bare = negative ? value.slice(1) : value;
    const [integerRaw = '0', fractionRaw = ''] = bare.split('.');
    let integer = integerRaw === '' ? '0' : integerRaw;
    let fraction = fractionRaw.slice(0, precision).padEnd(precision, '0');
    // Half-up on the first discarded digit.
    if ((fractionRaw[precision] ?? '0') >= '5') {
        const carried = String(BigInt(`${integer}${fraction}` || '0') + 1n).padStart(integer.length + precision, '0');
        integer = precision === 0 ? carried : carried.slice(0, carried.length - precision);
        fraction = precision === 0 ? '' : carried.slice(carried.length - precision);
    }
    const magnitude = precision === 0 ? integer : `${integer}.${fraction}`;
    // "-0.000" is a rounding artefact, never a quantity a person wrote.
    const signed = negative && /[1-9]/.test(magnitude) ? `-${magnitude}` : magnitude;
    return signed;
}
/**
 * `N-4`'s second half, and `N-14`'s: "a quantity on a row must ALWAYS be shown
 * with its unit, and never added to the row above."
 *
 * ── WHY THE SYMBOL IS ON THE ROW AND NOT IN THE COLUMN HEADER ──────────────
 * A header would be shorter, and on a single-item screen it would even read
 * better. It is still wrong, for the reason `N-14` gives: "items are counted in
 * pieces, kilograms, litres, metres and boxes", so the rows of one stock table
 * do not share a unit and a header can only be true of some of them. Worse, a
 * unit-less column of numbers is an invitation to add it up — which is the one
 * thing `N-14` forbids ("adding 3 kg to 4 boxes is meaningless"). The symbol
 * beside every number is what makes that mistake visible instead of tidy.
 *
 * `units.symbol` is the tenant's own short mark, and MD-12 describes it as
 * exactly this: "the short mark printed beside every quantity (`4 drum`)".
 *
 * The space is a plain one, not a non-breaking one: on screen the pair travels
 * inside a single `<bdi dir="ltr">` island (`<Qty>` in the SPA, `ltr()` here),
 * so the bidi algorithm keeps the number and its symbol together in Arabic
 * without a joiner. See `apps/web/src/components/amount.tsx`.
 */
export function formatQtyWithUnit(value, precision, unitSymbol) {
    const qty = formatQty(value, precision);
    return unitSymbol === '' ? qty : `${qty} ${unitSymbol}`;
}
/**
 * Dates on a document are ISO, in both languages, for the same reason the
 * digits are Latin: a document is a record, and 2026-08-13 cannot be read as
 * either 8 August or 13 August by mistake. The Gregorian/Hijri question is a
 * PART 15 org setting that does not exist yet; when it does, it arrives here.
 */
export function formatDocumentDate(iso) {
    return iso.slice(0, 10);
}
/**
 * An ISO instant as "2026-08-25 14:32 UTC" — BR-DSH-10-16's "run time".
 *
 * The zone is PRINTED, not assumed away: `generatedAt` is a UTC instant
 * (`new Date().toISOString()` in the browser, the server clock in the
 * worker), and a bare "14:32" on a report an auditor holds would be a claim
 * about somebody's wall clock that nothing here can honour — the company
 * clock (N-16) is a DATE discipline and carries no time-of-day API to this
 * package. A labelled UTC time is exact for everyone; an unlabelled local
 * time is exact for no one. When a company time-of-day setting exists (PART
 * 15), it arrives here beside the Gregorian/Hijri question above.
 */
export function formatDocumentInstant(iso) {
    const time = iso.slice(11, 16);
    return time === '' ? iso.slice(0, 10) : `${iso.slice(0, 10)} ${time} UTC`;
}
//# sourceMappingURL=format.js.map